/**
 * `docs/DESIGN.md`, "Solo": "survival — a garbage row pushes the stack up on
 * a cadence set by the hardness level. Score is survival time (and lines
 * cleared as the tiebreak display)."
 *
 * The hardness table gives survival two things per level: the gravity speed
 * (shared with sprint) and the row cadence (10 s / 8 s / 6 s / 4 s).
 *
 * The floor is `@interlock/sim`'s `risingFloorRule`, the same rule
 * `duelSurvival` and `coopWide` run: a row on every `everyTicks`-th tick,
 * before that tick's inputs, pushed straight in. It is a rule of the tick and
 * not a timer in `ModeRuntime`, because it changes the board, and what changes
 * the board has to be a function of the tick and nothing else. A clear cannot
 * cancel it. `docs/decisions/0050`.
 */
import { TICKS_PER_SECOND } from '@interlock/engine';
import { risingFloorRule, ticksUntilFloorRow } from '@interlock/sim';

import {
  HARDNESS_GRAVITY_PERMILLE,
  HARDNESS_SURVIVAL_INTERVAL_MS,
  survivalFloorTicks,
} from './config.js';
import type { ModeConfig } from './config.js';
import { formatClock } from './format.js';
import type { ModeDescriptor } from './types.js';

export function buildSurvivalMode(config: ModeConfig): ModeDescriptor {
  const everyTicks = survivalFloorTicks(config.hardness);
  const speedPermille = HARDNESS_GRAVITY_PERMILLE[config.hardness];

  return {
    id: 'survival',
    copy: {
      name: 'Survival',
      tagline: 'The floor never stops rising.',
      goal: `One garbage row pushes up every ${String(HARDNESS_SURVIVAL_INTERVAL_MS[config.hardness] / 1000)} seconds. Survive as long as you can.`,
    },
    engineOptions: { gravity: { speedPermille } },
    tickRule: risingFloorRule(everyTicks),

    onTick(_state, runtime, tickMs) {
      runtime.elapsedMs += tickMs;
    },

    checkEnd(state, runtime) {
      if (state.topOut) {
        const seconds = (runtime.elapsedMs / 1000).toFixed(1);
        return {
          result: 'lose',
          title: 'Buried',
          summary: `${String(state.lines)} lines cleared, survived ${seconds}s.`,
        };
      }
      return null;
    },

    hud: [
      { id: 'lines', label: 'Lines', select: (state) => String(state.lines) },
      {
        id: 'next-row',
        label: 'Next row',
        select: (state) =>
          `${(ticksUntilFloorRow(state.tick, everyTicks) / TICKS_PER_SECOND).toFixed(1)}s`,
      },
    ],

    // Every run is worth recording — the whole point of survival is "how
    // long did you last," so unlike sprint there is no "didn't finish" case
    // to exclude; topping out immediately is still a real (short) score.
    score(_state, runtime) {
      return {
        value: runtime.elapsedMs,
        betterWhen: 'higher',
        formatted: formatClock(runtime.elapsedMs),
      };
    },
  };
}
